/**
 * Drone Fleet Timeline Component - Phase X2
 * 
 * Custom CSS Grid-based Gantt-style timeline for fleet-wide view.
 * Rows = drones, Columns = time periods.
 */

import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Loader2, Circle, Wrench
} from 'lucide-react';
import {
  STATUS_COLORS,
  SAIL_COLORS,
  getSailBadgeLabel,
  getTelemetryIndicator,
  calculateEventPosition
} from '../../lib/calendar-utils';

/**
 * Timeline row for a single drone with 15-min snap support for day view
 */
const DroneRow = ({ 
  drone, 
  events, 
  columns, 
  rangeStart, 
  rangeEnd,
  onEventClick,
  onSlotClick,
  language = 'en',
  view = 'week'
}) => {
  const telemetry = getTelemetryIndicator(drone.telemetry_status);
  const [hoveredCol, setHoveredCol] = useState(null);
  
  // Calculate event positions
  const eventBars = events.map(event => {
    const start = new Date(event.scheduled_start || event.mission_date);
    const end = new Date(event.scheduled_end || dayjs(start).add(2, 'hour').toDate());
    const position = calculateEventPosition(start, end, rangeStart, rangeEnd);
    
    return {
      ...event,
      position,
      startDate: start,
      endDate: end
    };
  });
  
  // Snap to nearest 15-minute boundary (rounding down)
  const snapTo15Min = (minutes) => Math.floor(minutes / 15) * 15;
  
  const handleSlotClick = (e, columnIndex, col) => {
    if (!onSlotClick) return;
    
    let slotStart;
    if (view === 'day') {
      // Day view: columns are hours, click position determines minute
      // col already has minute=0 from column generation, but ensure clean boundary
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const cellWidth = rect.width;
      const minuteOffset = snapTo15Min(Math.floor((relativeX / cellWidth) * 60));
      slotStart = col.minute(minuteOffset).second(0).millisecond(0).toDate();
    } else {
      // Week/Month view: columns are days
      slotStart = dayjs(rangeStart).add(columnIndex, 'day').hour(9).minute(0).second(0).millisecond(0).toDate();
    }
    
    const slotEnd = dayjs(slotStart).add(2, 'hour').toDate();
    onSlotClick({
      aircraft_id: drone.id,
      etot: slotStart.toISOString(),
      eldt: slotEnd.toISOString()
    });
  };
  
  const handleMouseMove = (e, columnIndex, col) => {
    if (view !== 'day') {
      setHoveredCol(null);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const cellWidth = rect.width;
    const minuteOffset = snapTo15Min(Math.floor((relativeX / cellWidth) * 60));
    const percentLeft = (minuteOffset / 60) * 100;
    const hour = col.hour();
    
    setHoveredCol({
      index: columnIndex,
      minute: minuteOffset,
      percentLeft,
      timeStr: `${String(hour).padStart(2, '0')}:${String(minuteOffset).padStart(2, '0')}`
    });
  };
  
  return (
    <div className="flex border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      {/* Drone info column */}
      <div className="w-48 flex-shrink-0 p-2 border-r border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Circle 
            className={`h-2 w-2 ${telemetry.pulse ? 'animate-pulse' : ''}`}
            style={{ fill: telemetry.color, color: telemetry.color }}
          />
          <div className="truncate">
            <div className="font-medium text-sm truncate" title={drone.model}>
              {drone.model}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {drone.registration_number || drone.serial_number}
            </div>
          </div>
        </div>
      </div>
      
      {/* Timeline area */}
      <div className="flex-1 relative min-h-[48px]" style={{ minWidth: columns.length * 40 }}>
        {/* Grid lines */}
        <div className="absolute inset-0 flex">
          {columns.map((col, i) => {
            const isHovered = hoveredCol?.index === i;
            
            return (
              <div 
                key={i}
                className="flex-1 border-r border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 relative group"
                onClick={(e) => handleSlotClick(e, i, col)}
                onMouseMove={(e) => handleMouseMove(e, i, col)}
                onMouseLeave={() => setHoveredCol(null)}
              >
                {/* 15-min grid lines for day view (visible on hover) */}
                {view === 'day' && (
                  <div className="absolute inset-0 flex opacity-0 group-hover:opacity-100 pointer-events-none">
                    <div className="flex-1 border-r border-gray-200 dark:border-gray-600" />
                    <div className="flex-1 border-r border-gray-200 dark:border-gray-600" />
                    <div className="flex-1 border-r border-gray-200 dark:border-gray-600" />
                    <div className="flex-1" />
                  </div>
                )}
                
                {/* Hover time indicator */}
                {isHovered && view === 'day' && (
                  <>
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-blue-500 pointer-events-none z-10"
                      style={{ left: `${hoveredCol.percentLeft}%` }}
                    />
                    <div 
                      className="absolute -top-5 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded pointer-events-none z-20 whitespace-nowrap"
                      style={{ left: `${hoveredCol.percentLeft}%`, transform: 'translateX(-50%)' }}
                    >
                      {hoveredCol.timeStr}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Event bars */}
        {eventBars.map((event, idx) => {
          const isMaintenance = event.event_type === 'maintenance';
          const bgColor = isMaintenance ? '#9ca3af' : (STATUS_COLORS[event.status] || STATUS_COLORS.draft);
          
          // Maintenance has diagonal stripe pattern and lower z-index
          const stripeStyle = isMaintenance ? {
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 3px,
              rgba(255,255,255,0.15) 3px,
              rgba(255,255,255,0.15) 6px
            )`
          } : {};
          
          const zIndex = isMaintenance ? 4 : 5; // Maintenance under missions
          
          return (
            <div
              key={event.maintenance_id || event.mission_id || idx}
              className={`absolute top-1 bottom-1 rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center px-1 overflow-hidden ${isMaintenance ? 'opacity-85' : ''}`}
              style={{
                left: `${event.position.left}%`,
                width: `${Math.max(event.position.width, 2)}%`,
                backgroundColor: bgColor,
                minWidth: '20px',
                zIndex,
                ...stripeStyle
              }}
              onClick={() => onEventClick?.(event)}
              title={isMaintenance ? (event.reason || 'Maintenance') : `${event.mission_code} - ${event.mission_name}`}
              data-testid={isMaintenance ? 'maintenance-bar' : 'mission-bar'}
            >
              {isMaintenance ? (
                <>
                  <Wrench className="h-3 w-3 text-white mr-0.5 flex-shrink-0" />
                  <span className="text-[10px] text-white truncate font-medium">
                    {language === 'cs' ? 'Údržba' : 'Maint.'}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[10px] text-white truncate font-medium">
                    {event.mission_code}
                  </span>
                  {event.sail_class && (
                    <Badge
                      className="ml-1 text-[8px] px-0.5 py-0 h-3"
                      style={{ backgroundColor: SAIL_COLORS[event.sail_class] || '#9ca3af' }}
                      title={event.sail_provisional ? (language === 'cs' ? 'Předběžný odhad ze zjednodušeného výpočtu' : 'Preliminary estimate from a simplified calculation') : undefined}
                    >
                      {getSailBadgeLabel(event.sail_class, language, event.sail_provisional)}
                    </Badge>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Timeline header with date columns
 */
const TimelineHeader = ({ columns, view, language, translations }) => {
  dayjs.locale(language === 'cs' ? 'cs' : 'en');
  const droneLabel = translations?.drone || (language === 'cs' ? 'Dron' : 'Drone');
  
  return (
    <div className="flex border-b-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 sticky top-0 z-10">
      {/* Drone label column */}
      <div className="w-48 flex-shrink-0 p-2 border-r border-gray-200 dark:border-gray-700 font-semibold text-sm">
        {droneLabel}
      </div>
      
      {/* Date columns */}
      <div className="flex-1 flex" style={{ minWidth: columns.length * 40 }}>
        {columns.map((col, i) => {
          const isWeekend = [0, 6].includes(col.day());
          const isToday = col.isSame(dayjs(), 'day');
          
          return (
            <div 
              key={i}
              className={`flex-1 text-center text-xs py-1 border-r border-gray-100 dark:border-gray-800
                ${isWeekend ? 'bg-gray-100 dark:bg-gray-700/50' : ''}
                ${isToday ? 'bg-blue-100 dark:bg-blue-900/30 font-bold' : ''}
              `}
            >
              {/* Force clean hour display: HH:00 format */}
              {view === 'day' ? `${String(col.hour()).padStart(2, '0')}:00` : col.format('D')}
              {view !== 'day' && (
                <div className="text-[10px] text-gray-500">
                  {col.format('ddd')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DroneFleetTimeline = ({
  drones = [],
  loading = false,
  language = 'en',
  translations = {},
  onEventClick,
  onSlotClick,
  onNavigate,
  initialDate = new Date()
}) => {
  const [view, setView] = useState('week'); // day, week, month
  const [currentDate, setCurrentDate] = useState(dayjs(initialDate));
  const [show24h, setShow24h] = useState(false);
  
  dayjs.locale(language === 'cs' ? 'cs' : 'en');
  
  // Use translations from props, fallback to inline only if missing
  const t = {
    today: translations.today || (language === 'cs' ? 'Dnes' : 'Today'),
    day: translations.day || (language === 'cs' ? 'Den' : 'Day'),
    week: translations.week || (language === 'cs' ? 'Týden' : 'Week'),
    month: translations.month || (language === 'cs' ? 'Měsíc' : 'Month'),
    drones: translations.drones || (language === 'cs' ? 'dronů' : 'drones'),
    noData: translations.noDrones || (language === 'cs' ? 'Žádné drony' : 'No drones'),
    drone: translations.drone || (language === 'cs' ? 'Dron' : 'Drone'),
    status: translations.status || (language === 'cs' ? 'Stav' : 'Status'),
    telemetry: translations.telemetry || (language === 'cs' ? 'Telemetrie' : 'Telemetry'),
    fleetOverview: translations.fleetOverview || (language === 'cs' ? 'Přehled flotily' : 'Fleet Overview'),
    draft: translations.draft || (language === 'cs' ? 'Koncept' : 'Draft'),
    ready: translations.ready || (language === 'cs' ? 'Připraveno' : 'Ready'),
    active: translations.active || (language === 'cs' ? 'Probíhá' : 'Active'),
    completed: translations.completed || (language === 'cs' ? 'Dokončeno' : 'Done'),
    live: translations.live || 'Live',
    offline: translations.offline || 'Offline',
    show24h: translations.show24h || (language === 'cs' ? 'Zobrazit celých 24h' : 'Show full 24h'),
    show16h: translations.show16h || (language === 'cs' ? 'Zobrazit 06-22h' : 'Show 06-22h')
  };
  
  // Generate columns based on view
  const { columns, rangeStart, rangeEnd } = useMemo(() => {
    let cols = [];
    let start, end;
    
    switch (view) {
      case 'day':
        start = currentDate.startOf('day');
        end = currentDate.endOf('day');
        // Hour columns with 15-min subdivisions (default 06-22, or 00-23 if 24h)
        // IMPORTANT: Force clean hour boundaries with minute=0, second=0, ms=0
        const startHour = show24h ? 0 : 6;
        const endHour = show24h ? 23 : 21;
        for (let h = startHour; h <= endHour; h++) {
          cols.push(currentDate.hour(h).minute(0).second(0).millisecond(0));
        }
        break;
      case 'week':
        start = currentDate.startOf('week');
        end = currentDate.endOf('week');
        for (let d = 0; d < 7; d++) {
          cols.push(start.add(d, 'day'));
        }
        break;
      case 'month':
      default:
        start = currentDate.startOf('month');
        end = currentDate.endOf('month');
        const daysInMonth = currentDate.daysInMonth();
        for (let d = 1; d <= daysInMonth; d++) {
          cols.push(currentDate.date(d));
        }
        break;
    }
    
    return { 
      columns: cols, 
      rangeStart: start.toDate(), 
      rangeEnd: end.toDate() 
    };
  }, [view, currentDate, show24h]);
  
  // Navigation handlers
  const navigate = useCallback((direction) => {
    let newDate;
    switch (view) {
      case 'day':
        newDate = direction === 'prev' 
          ? currentDate.subtract(1, 'day') 
          : currentDate.add(1, 'day');
        break;
      case 'week':
        newDate = direction === 'prev' 
          ? currentDate.subtract(1, 'week') 
          : currentDate.add(1, 'week');
        break;
      case 'month':
      default:
        newDate = direction === 'prev' 
          ? currentDate.subtract(1, 'month') 
          : currentDate.add(1, 'month');
        break;
    }
    setCurrentDate(newDate);
    onNavigate?.(newDate.toDate(), view);
  }, [view, currentDate, onNavigate]);
  
  const goToToday = useCallback(() => {
    setCurrentDate(dayjs());
    onNavigate?.(new Date(), view);
  }, [view, onNavigate]);
  
  // Format header label
  const headerLabel = useMemo(() => {
    switch (view) {
      case 'day':
        return currentDate.format('dddd, D. MMMM YYYY');
      case 'week':
        const weekStart = currentDate.startOf('week');
        const weekEnd = currentDate.endOf('week');
        return `${weekStart.format('D. MMM')} - ${weekEnd.format('D. MMM YYYY')}`;
      case 'month':
      default:
        return currentDate.format('MMMM YYYY');
    }
  }, [view, currentDate]);
  
  return (
    <Card className="border-[var(--border-default)]" data-testid="drone-fleet-timeline">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2" data-testid="fleet-title">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            {t.fleetOverview}
          </CardTitle>
          <div className="text-sm text-[var(--text-muted)]">
            {drones.length} {t.drones}
          </div>
        </div>
        
        {/* Toolbar */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday}>
              {t.today}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            {view === 'day' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShow24h(!show24h)}
                className="ml-2 text-xs"
                data-testid="toggle-24h-fleet"
              >
                {show24h ? t.show16h : t.show24h}
              </Button>
            )}
          </div>
          
          <span className="font-semibold">{headerLabel}</span>
          
          <div className="flex items-center gap-1">
            {['day', 'week', 'month'].map(v => (
              <Button
                key={v}
                variant={view === v ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setView(v);
                  onNavigate?.(currentDate.toDate(), v);
                }}
              >
                {t[v]}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {loading ? (
          <div className="h-[400px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : drones.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-gray-500">
            {t.noData}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <div className="min-w-fit">
              <TimelineHeader 
                columns={columns} 
                view={view} 
                language={language}
                translations={t}
              />
              
              {drones.map(drone => (
                <DroneRow
                  key={drone.id}
                  drone={drone}
                  events={drone.events || []}
                  columns={columns}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  onEventClick={onEventClick}
                  onSlotClick={onSlotClick}
                  language={language}
                  view={view}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Legend */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-4 text-xs" data-testid="timeline-legend">
          <div className="flex items-center gap-2">
            <span className="font-medium">{t.status}:</span>
            {Object.entries(STATUS_COLORS).map(([status, color]) => (
              <span key={status} className="flex items-center gap-1">
                <span 
                  className="w-3 h-3 rounded" 
                  style={{ backgroundColor: color }}
                />
                {t[status] || status}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{t.telemetry}:</span>
            <span className="flex items-center gap-1">
              <Circle className="h-2 w-2" style={{ fill: '#22c55e', color: '#22c55e' }} />
              {t.live}
            </span>
            <span className="flex items-center gap-1">
              <Circle className="h-2 w-2" style={{ fill: '#9ca3af', color: '#9ca3af' }} />
              {t.offline}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DroneFleetTimeline;
